import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  ApiError,
  changeMePassword,
  confirmEmailVerification,
  getAuthMode,
  getMeSecurity,
  getMeTrustedDevices,
  logout as apiLogout,
  patchMeSecurity,
  patchMeProfile,
  removeMeProfilePhoto,
  requestMeEmailVerification,
  revokeAllMeSessions,
  revokeMeTrustedDevice,
  saveScope,
  uploadMeProfilePhoto,
} from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { KratosSettingsPanel } from "@/features/auth/kratos/KratosSettingsPanel";
import {
  applyExplicitLogoutBoundary,
  removeAllScopedFromCache,
  unscopedKey,
} from "@/lib/queryClient";
import { revokeAllPlayerPhotos } from "@/features/player-photos/usePlayerPhoto";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import type {
  AuthenticationMethodSummary,
  MePayload,
  MeSecurityPayload,
  MeTeam,
  UserProfile,
} from "@/lib/types";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import {
  invalidateAccountPhoto,
  useAccountPhoto,
} from "@/features/account/useAccountPhoto";
import { ConsentTab } from "@/features/account/components/ConsentTab";
import { LinkedAthletesPanel } from "@/features/account/components/LinkedAthletesPanel";
import {
  isAccessRequestEmailEnabled,
  isFeedbackDigestEmailEnabled,
  isFocusModeEnabled,
} from "@/features/account/preferences";
import {
  AtSign,
  BellRing,
  Camera,
  CheckCircle2,
  Clock3,
  Globe2,
  FileCheck2,
  Fingerprint,
  ImagePlus,
  KeyRound,
  Laptop,
  Link2,
  LockKeyhole,
  Mail,
  Maximize2,
  Phone,
  ShieldCheck,
  Trash2,
  UserRound,
  UsersRound,
} from "lucide-react";

/**
 * M9a B1–B8 — `/me` account self-service shell.
 *
 * Four tabs (Profile · Security · Email · Sessions). `?tab=` route
 * search-param drives the active tab; `.catch(undefined)` falls
 * back to `profile`.
 *
 * Auth gate (B7): unauthenticated users redirect to
 * `/login?next=/me` (preserves their landing intent). The redirect
 * fires from a `useEffect` so the SPA shell still mounts on a
 * direct nav.
 */

type AccountTab = "profile" | "security" | "email" | "sessions" | "consent";
const VALID_TABS: AccountTab[] = [
  "profile",
  "security",
  "email",
  "sessions",
  "consent",
];

const TAB_ICONS: Record<AccountTab, ReactNode> = {
  profile: <UserRound size={15} strokeWidth={2.2} aria-hidden="true" />,
  security: <ShieldCheck size={15} strokeWidth={2.2} aria-hidden="true" />,
  email: <Mail size={15} strokeWidth={2.2} aria-hidden="true" />,
  sessions: <Laptop size={15} strokeWidth={2.2} aria-hidden="true" />,
  consent: <FileCheck2 size={15} strokeWidth={2.2} aria-hidden="true" />,
};

const COMMON_TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Athens",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

const COMMON_LOCALES = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "es-ES", label: "Español" },
  { value: "fr-FR", label: "Français" },
  { value: "de-DE", label: "Deutsch" },
  { value: "pt-BR", label: "Português (BR)" },
  { value: "ja-JP", label: "日本語" },
];

export function AccountPage() {
  const me = useMe();
  const navigate = useNavigate({ from: "/me" });
  const search = useSearch({ from: "/me" });
  const requestedTab = search.tab === "password" ? "security" : search.tab;
  const tab: AccountTab = VALID_TABS.includes(requestedTab as AccountTab)
    ? (requestedTab as AccountTab)
    : "profile";

  // B7 — auth gate: unauth → /login?next=/me. Ref-flag prevents
  // the effect re-firing once the URL changes (TanStack Router
  // keeps the parent briefly mounted on intra-effect navigate).
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (me.isLoading) return;
    if (me.data?.user) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: "/me" },
      replace: true,
    });
  }, [me.isLoading, me.data, navigate]);

  if (me.isLoading) {
    return (
      <main
        data-testid="me-loading"
        className="min-h-screen flex items-center justify-center text-ink-muted"
      >
        Loading…
      </main>
    );
  }
  if (!me.data?.user) {
    return null;
  }

  return (
    <main data-testid="me-page" className="min-h-screen bg-canvas">
      <div className="account-page-inner">
        <AccountHeader />
        <AccountIdentityStrip me={me.data} />

        <nav
          data-testid="me-tabs"
          role="tablist"
          aria-label="Account settings"
          className="account-tabs"
        >
          <div>
            {VALID_TABS.map((t) => (
              <TabButton
                key={t}
                active={tab === t}
                icon={TAB_ICONS[t]}
                onClick={() =>
                  navigate({
                    search: (prev) => ({ ...prev, tab: t }),
                    replace: false,
                  })
                }
                testid={`me-tab-${t}`}
              >
                {labelForTab(t)}
              </TabButton>
            ))}
          </div>
        </nav>

        <div className="account-content-layout">
          <section
            data-testid="me-content"
            data-active-tab={tab}
            className="account-content"
          >
            {tab === "profile" ? <ProfileTab me={me.data} /> : null}
            {tab === "security" ? <SecurityTab /> : null}
            {tab === "email" ? <EmailTab me={me.data} /> : null}
            {tab === "sessions" ? <SessionsTab /> : null}
            {tab === "consent" ? <ConsentTab /> : null}
          </section>
        </div>
      </div>
    </main>
  );
}

function labelForTab(tab: AccountTab): string {
  return tab.charAt(0).toUpperCase() + tab.slice(1);
}

function TabButton({
  active,
  icon,
  onClick,
  children,
  testid,
}: {
  active: boolean;
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
  testid: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : undefined}
      className="account-tab-button"
    >
      {icon}
      {children}
    </button>
  );
}

function AccountHeader() {
  return (
    <header className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
      <div className="min-w-0">
        <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
          Your account
        </p>
        <h1 className="font-display text-2xl uppercase tracking-wider text-ink sm:text-3xl">
          Make yourself at home.
        </h1>
        <p className="mt-1 max-w-3xl text-sm leading-snug text-ink-muted">
          Profile, sign-in and preferences for your Tktic account.
        </p>
      </div>
    </header>
  );
}

export function AccountSummary({ me }: { me: MePayload }) {
  const displayName =
    me.user.display_name || me.profile.display_name || me.user.username;
  const initials = getInitials(displayName);
  const accountPhotoUrl = useAccountPhoto(me.profile.photo_filename);
  const authority = profileAuthoritySummary(me);
  const activeTeam = me.active_scope?.team?.name ?? "No active team";
  const contact = me.profile.email || me.profile.phone || "No contact on file";
  const visibleTeams = me.teams.slice(0, 4);
  const extraTeams = Math.max(0, me.teams.length - visibleTeams.length);
  return (
    <aside
      data-testid="me-account-summary"
      className="rounded border border-rule bg-surface p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        {accountPhotoUrl ? (
          <img
            src={accountPhotoUrl}
            alt=""
            className="h-12 w-12 shrink-0 rounded-full border border-rule object-cover"
          />
        ) : (
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-ink font-display text-base font-semibold text-ink-inverse">
            {initials}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate font-display text-base font-semibold text-ink">
            {displayName}
          </p>
          <p className="truncate font-mono text-xs text-ink-muted">
            @{me.user.username}
          </p>
        </div>
      </div>
      <div className="mt-4 grid gap-2">
        {authority.roleAuthority.summaryLabel === "Access" ? (
          <SummaryRow
            icon={<ShieldCheck size={15} strokeWidth={2.2} />}
            label={authority.roleAuthority.summaryLabel}
            value={authority.roleAuthority.role}
          />
        ) : null}
        {authority.clubAuthority ? (
          <SummaryRow
            icon={<UsersRound size={15} strokeWidth={2.2} />}
            label="Club authority"
            value={authority.clubAuthority}
          />
        ) : null}
        <SummaryRow
          icon={<Globe2 size={15} strokeWidth={2.2} />}
          label="Team"
          value={activeTeam}
        />
        <SummaryRow
          icon={
            me.profile.email ? (
              <AtSign size={15} strokeWidth={2.2} />
            ) : (
              <Phone size={15} strokeWidth={2.2} />
            )
          }
          label="Contact"
          value={contact}
        />
        <SummaryRow
          icon={<Clock3 size={15} strokeWidth={2.2} />}
          label="Timezone"
          value={me.profile.timezone || "Not set"}
        />
      </div>
      <div className="mt-4 rounded border border-rule bg-elevated p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="min-w-0 font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Assigned teams & roles
          </p>
          <span className="shrink-0 rounded-full border border-rule bg-surface px-2 py-0.5 font-mono text-[0.6rem] text-ink-muted">
            {me.teams.length}
          </span>
        </div>
        {visibleTeams.length > 0 ? (
          <div className="mt-2 grid gap-1.5">
            {visibleTeams.map((team) => {
              const roleLabels = profileTeamRoleLabels(me, team);
              return (
                <div
                  key={team.id}
                  data-testid="me-profile-summary-team-row"
                  data-team-id={team.id}
                  className="min-w-0 rounded border border-rule/70 bg-surface px-2 py-2"
                >
                  <p className="break-words text-xs leading-snug text-ink">
                    {team.name}
                  </p>
                  <div
                    data-testid="me-profile-summary-team-roles"
                    className="mt-1 flex min-w-0 flex-wrap gap-1"
                  >
                    {roleLabels.map((label) => (
                      <span
                        key={label}
                        data-testid="me-profile-summary-team-role"
                        className="rounded border border-rule bg-elevated px-1.5 py-0.5 font-display text-[0.56rem] uppercase tracking-wider text-ink-muted"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
            {extraTeams > 0 ? (
              <p className="px-1 text-xs text-ink-muted">
                +{extraTeams} more in Profile
              </p>
            ) : null}
          </div>
        ) : (
          <p className="mt-2 text-xs text-ink-muted">
            No team assignments yet.
          </p>
        )}
      </div>
    </aside>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded border border-rule bg-elevated px-3 py-2">
      <span className="text-ink-muted" aria-hidden="true">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block font-display text-[0.58rem] uppercase tracking-wider text-ink-muted">
          {label}
        </span>
        <span className="block truncate text-sm text-ink">{value}</span>
      </span>
    </div>
  );
}

function StatusPill({
  tone,
  children,
  testid,
  title,
}: {
  tone: "accent" | "good" | "muted";
  children: ReactNode;
  testid?: string;
  title?: string;
}) {
  const toneClass =
    tone === "accent"
      ? "border-accent bg-accent text-accent-ink"
      : tone === "good"
        ? "border-good/40 bg-good/10 text-good"
        : "border-rule bg-elevated text-ink-muted";
  return (
    <span
      data-testid={testid}
      title={title}
      className={`inline-flex h-8 min-w-0 max-w-full items-center rounded-full border px-3 font-display text-[0.65rem] font-semibold uppercase tracking-wider ${toneClass}`}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}

function SectionHeader({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: ReactNode;
}) {
  return (
    <header className="grid gap-3 border-b border-rule pb-4 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start">
      <span className="hidden h-10 w-10 items-center justify-center rounded bg-elevated text-ink-muted sm:inline-flex">
        {icon}
      </span>
      <div className="min-w-0">
        <h2 className="font-display text-lg uppercase tracking-wider text-ink">
          {title}
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-snug text-ink-muted">
          {description}
        </p>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// B2 + B3 — Profile tab
// ---------------------------------------------------------------------------

function ProfileTab({ me }: { me: MePayload }) {
  const profile = me.profile;
  const toast = useToast();
  const queryClient = useQueryClient();
  const applyAuth = useAuthStore((s) => s.applyMePayload);
  const applyScope = useScopeStore((s) => s.applyMePayload);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const accountPhotoUrl = useAccountPhoto(profile.photo_filename);
  const browserTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  const seedFromProfile = (p: UserProfile) => ({
    first_name: p.first_name ?? "",
    last_name: p.last_name ?? "",
    email: p.email ?? "",
    phone: p.phone ?? "",
    timezone: p.timezone ?? browserTz,
    locale: p.locale ?? "en-US",
    preferred_contact_method:
      (p.preferred_contact_method as "email" | "phone" | "none") ?? "none",
    feature_focus_mode_enabled: isFocusModeEnabled(p),
  });

  const [draft, setDraft] = useState(() => seedFromProfile(profile));
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track whether the form has unsaved edits. We only re-seed from
  // an upstream profile refetch when the draft is pristine, so a
  // background `/api/me` refresh (window-focus refetch, scope swap,
  // another mutation invalidating the cache) cannot wipe what the
  // user is typing.
  const [dirty, setDirty] = useState(false);
  // Snapshot the baseline used for dirty-comparison, refreshed each
  // time we reseed from the server.
  const baselineRef = useRef(seedFromProfile(profile));

  // Refetch reseed: only fire when the form has no unsaved edits.
  useEffect(() => {
    if (dirty) return;
    const seeded = seedFromProfile(profile);
    baselineRef.current = seeded;
    setDraft(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const updateField = <K extends keyof typeof draft>(
    key: K,
    value: (typeof draft)[K],
  ) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      // Recompute dirty against the last server snapshot, NOT the
      // initial mount value, so a save → reseed → no-op edit cycle
      // returns to pristine correctly.
      const base = baselineRef.current;
      const isDirty =
        next.first_name !== base.first_name ||
        next.last_name !== base.last_name ||
        next.email !== base.email ||
        next.phone !== base.phone ||
        next.timezone !== base.timezone ||
        next.locale !== base.locale ||
        next.preferred_contact_method !== base.preferred_contact_method ||
        next.feature_focus_mode_enabled !== base.feature_focus_mode_enabled;
      setDirty(isDirty);
      return next;
    });
  };

  // `null` clears the stored field on the backend; `undefined` would
  // be dropped by `model_dump(exclude_unset=True)` and leave the
  // server value untouched. Always send the typed value (trimmed),
  // mapping empty → null so blanking out a field actually persists.
  const toBody = () => {
    const norm = (s: string): string | null => {
      const t = s.trim();
      return t.length === 0 ? null : t;
    };
    return {
      first_name: norm(draft.first_name),
      last_name: norm(draft.last_name),
      email: norm(draft.email),
      phone: norm(draft.phone),
      timezone: norm(draft.timezone),
      locale: norm(draft.locale),
      preferred_contact_method: draft.preferred_contact_method,
      feature_focus_mode_enabled: draft.feature_focus_mode_enabled,
    };
  };

  const mut = useMutation({
    mutationFn: () => patchMeProfile(toBody()),
    onSuccess: (payload) => {
      mergeProfileIntoMe(payload.profile);
      setSuccess(true);
      setDirty(false);
      toast.success("Profile updated");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "That email is already in use.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't update profile", { description: formatErr(err) });
    },
  });

  const mergeProfileIntoMe = (nextProfile: UserProfile) => {
    const current =
      queryClient.getQueryData<MePayload>(unscopedKey("me")) ?? me;
    const nextMe = { ...current, profile: nextProfile };
    queryClient.setQueryData(unscopedKey("me"), nextMe);
    applyAuth(nextMe);
    applyScope(nextMe);
  };

  const uploadPhotoMutation = useMutation({
    mutationFn: (file: File) => uploadMeProfilePhoto(file),
    onSuccess: (payload) => {
      invalidateAccountPhoto();
      mergeProfileIntoMe(payload.profile);
      toast.success("Profile picture updated");
    },
    onError: (err) => {
      toast.error("Couldn't update profile picture", {
        description: formatErr(err),
      });
    },
    onSettled: () => {
      if (photoInputRef.current) photoInputRef.current.value = "";
    },
  });

  const removePhotoMutation = useMutation({
    mutationFn: removeMeProfilePhoto,
    onSuccess: (payload) => {
      invalidateAccountPhoto();
      mergeProfileIntoMe(payload.profile);
      toast.success("Profile picture removed");
    },
    onError: (err) => {
      toast.error("Couldn't remove profile picture", {
        description: formatErr(err),
      });
    },
  });

  const defaultTeamMutation = useMutation({
    mutationFn: (teamId: string) => saveScope({ team_id: teamId }),
    onSuccess: (payload) => {
      applyAuth(payload);
      applyScope(payload);
      queryClient.setQueryData(unscopedKey("me"), payload);
      removeAllScopedFromCache();
      revokeAllPlayerPhotos();
      toast.success("Default team updated");
    },
    onError: (err) => {
      toast.error("Couldn't update default team", {
        description: formatErr(err),
      });
    },
  });

  const profileHeading = (
    <SectionHeader
      icon={<UserRound size={18} strokeWidth={2.2} />}
      title="Your profile"
      description="The details your club uses to contact you."
    />
  );
  const profileFields = (
    <>
      {" "}
      <section
        data-testid="me-profile-app-preferences"
        className="rounded border border-rule bg-elevated p-3 sm:p-4"
      >
        <div className="mb-3 flex items-center gap-2">
          <Maximize2 size={16} strokeWidth={2.2} className="text-ink-muted" />
          <h3 className="font-display text-sm uppercase tracking-wider text-ink">
            App preferences
          </h3>
        </div>
        <PreferenceToggle
          label="Coach Review focus mode"
          description="Show the fullscreen Focus mode control in Coach Review."
          checked={draft.feature_focus_mode_enabled}
          disabled={mut.isPending}
          onChange={(next) => updateField("feature_focus_mode_enabled", next)}
          data-testid="me-profile-focus-mode-toggle"
        />
      </section>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextField
          label="First name"
          value={draft.first_name}
          onChange={(v) => updateField("first_name", v)}
          testid="me-profile-first-name"
          maxLength={120}
        />
        <TextField
          label="Last name"
          value={draft.last_name}
          onChange={(v) => updateField("last_name", v)}
          testid="me-profile-last-name"
          maxLength={120}
        />
      </div>
      <div className="account-field-pair contents">
        <TextField
          label="Email"
          type="email"
          value={draft.email}
          onChange={(v) => updateField("email", v)}
          testid="me-profile-email"
          autoComplete="email"
          maxLength={320}
        />
        <TextField
          label="Phone"
          type="tel"
          value={draft.phone}
          onChange={(v) => updateField("phone", v)}
          testid="me-profile-phone"
          autoComplete="tel"
          maxLength={64}
        />
      </div>
      <div className="account-field-pair contents">
        <label className="flex flex-col gap-1.5 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Timezone
          </span>
          {/* Make the browser-detected tz the first option when it's
           * outside the curated COMMON_TIMEZONES list. */}
          <TkticSelect
            value={draft.timezone}
            onChange={(next) => updateField("timezone", next)}
            data-testid="me-profile-timezone"
            ariaLabel="Timezone"
            options={[
              ...(!COMMON_TIMEZONES.includes(draft.timezone) && draft.timezone
                ? [
                    {
                      value: draft.timezone,
                      label: `${draft.timezone} (detected)`,
                    },
                  ]
                : []),
              ...COMMON_TIMEZONES.map((tz) => ({ value: tz, label: tz })),
            ]}
          />
        </label>

        <label className="flex flex-col gap-1.5 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Language
          </span>
          <TkticSelect
            value={draft.locale}
            onChange={(next) => updateField("locale", next)}
            data-testid="me-profile-locale"
            ariaLabel="Language"
            options={COMMON_LOCALES.map((l) => ({
              value: l.value,
              label: l.label,
            }))}
          />
        </label>
      </div>
      <div
        data-testid="me-profile-contact"
        className="flex flex-col gap-1.5 text-xs"
      >
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Preferred contact method
        </span>
        <TkticSelect
          ariaLabel="Preferred contact method"
          value={draft.preferred_contact_method}
          onChange={(v) =>
            updateField(
              "preferred_contact_method",
              v as "email" | "phone" | "none",
            )
          }
          options={[
            { value: "email", label: "Email" },
            { value: "phone", label: "Phone" },
            { value: "none", label: "None" },
          ]}
        />
      </div>
      {error ? (
        <p
          data-testid="me-profile-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="me-profile-success"
          className="text-good text-xs font-mono"
        >
          Profile saved.
        </p>
      ) : null}
      <div className="account-profile-save-actions flex justify-end border-t border-rule pt-4">
        <Button
          type="submit"
          size="sm"
          disabled={mut.isPending}
          data-testid="me-profile-save"
        >
          <CheckCircle2 size={15} strokeWidth={2.2} />
          {mut.isPending ? "Saving…" : "Save profile"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={!dirty || mut.isPending}
          onClick={() => {
            setDraft(seedFromProfile(profile));
            setDirty(false);
            setError(null);
            setSuccess(false);
          }}
        >
          Discard changes
        </Button>
      </div>
    </>
  );
  const profileSide = (
    <>
      {" "}
      <section
        data-testid="me-profile-photo-panel"
        className="grid gap-4 rounded border border-rule bg-elevated p-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center sm:p-4"
      >
        <div className="relative h-20 w-20 shrink-0">
          {accountPhotoUrl ? (
            <img
              src={accountPhotoUrl}
              alt=""
              className="h-20 w-20 rounded-full border border-rule object-cover"
            />
          ) : (
            <span className="flex h-20 w-20 items-center justify-center rounded-full border border-rule bg-surface font-display text-xl font-semibold text-ink">
              {getInitials(
                me.user.display_name ||
                  profile.display_name ||
                  me.user.username,
              )}
            </span>
          )}
          <span className="absolute -bottom-1 -right-1 inline-flex h-8 w-8 items-center justify-center rounded-full border border-rule bg-surface text-ink-muted shadow-sm">
            <Camera size={15} strokeWidth={2.2} aria-hidden="true" />
          </span>
        </div>
        <div className="min-w-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h3 className="font-display text-sm uppercase tracking-wider text-ink">
                A face to the name.
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-snug text-ink-muted">
                This picture appears on your account only. It is separate from
                roster player headshots and photo requests.
              </p>
              <p className="mt-2 font-mono text-[0.68rem] uppercase tracking-wider text-ink-muted">
                PNG, JPG, JPEG, or WebP · max 5 MB
              </p>
            </div>
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                data-testid="me-profile-photo-input"
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  if (file) uploadPhotoMutation.mutate(file);
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={
                  uploadPhotoMutation.isPending || removePhotoMutation.isPending
                }
                data-testid="me-profile-photo-upload"
                onClick={() => photoInputRef.current?.click()}
              >
                <ImagePlus size={15} strokeWidth={2.2} />
                {profile.photo_filename ? "Replace" : "Add a photo"}
              </Button>
              {profile.photo_filename ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={
                    uploadPhotoMutation.isPending ||
                    removePhotoMutation.isPending
                  }
                  data-testid="me-profile-photo-remove"
                  onClick={() => removePhotoMutation.mutate()}
                >
                  <Trash2 size={15} strokeWidth={2.2} />
                  Remove
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </section>
      <TeamAssignmentsPanel
        me={me}
        pendingTeamId={defaultTeamMutation.variables ?? null}
        saving={defaultTeamMutation.isPending}
        onSetDefault={(teamId) => defaultTeamMutation.mutate(teamId)}
      />
      <LinkedAthletesPanel me={me} />
    </>
  );
  return (
    <form
      data-testid="me-profile-form"
      onSubmit={(e) => {
        e.preventDefault();
        setSuccess(false);
        setError(null);
        mut.mutate();
      }}
      className="account-profile-layout flex flex-col gap-4"
    >
      <div className="account-profile-main flex flex-col gap-4">
        {profileHeading}
        {profileFields}
      </div>
      <aside className="account-profile-side flex flex-col gap-4">
        {profileSide}
      </aside>
    </form>
  );
}

function TeamAssignmentsPanel({
  me,
  pendingTeamId,
  saving,
  onSetDefault,
}: {
  me: MePayload;
  pendingTeamId: string | null;
  saving: boolean;
  onSetDefault: (teamId: string) => void;
}) {
  const defaultTeamId = profileDefaultTeamId(me);
  const groups = profileTeamSeasonGroups(me);
  const activeTeams = me.teams.filter((team) => !isArchivedTeam(team));
  const defaultTeam = me.teams.find((team) => team.id === defaultTeamId);
  const defaultIsArchived = !!defaultTeam && isArchivedTeam(defaultTeam);
  // "Set default" prefers a non-archived team: offer it between active
  // teams, and also when the stored default is archived so the user can
  // move it onto an active one.
  const offerSetDefault =
    activeTeams.length > 1 || (defaultIsArchived && activeTeams.length > 0);
  const currentTeamId = me.active_scope?.team.id ?? null;
  return (
    <section data-testid="me-profile-teams" className="profile-team-panel">
      <h3>Team assignments</h3>

      {defaultIsArchived ? (
        <p
          data-testid="me-profile-default-archived-hint"
          className="mt-3 rounded-inset border border-rule bg-elevated px-3 py-2 text-xs text-ink-muted"
        >
          Your default team is archived. Pick an active team as your default.
        </p>
      ) : null}

      {me.teams.length > 0 ? (
        <div className="profile-team-list">
          {groups.map((group) => {
            const isCurrent =
              currentTeamId !== null &&
              group.teams.some((team) => team.id === currentTeamId);
            return (
              <div
                key={group.key}
                data-testid="me-profile-season-group"
                data-season-id={group.seasonId ?? "legacy"}
              >
                <p className="flex items-center gap-2 pt-3 font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
                  <span data-testid="me-profile-season-group-label">
                    {group.label}
                  </span>
                  {isCurrent ? <Badge variant="outline">Current</Badge> : null}
                </p>
                {group.teams.map((team) => {
                  const archived = isArchivedTeam(team);
                  const isDefault = defaultTeamId === team.id;
                  const pending = saving && pendingTeamId === team.id;
                  const roleLabels = profileTeamRoleLabels(me, team);
                  return (
                    <div
                      key={team.id}
                      data-testid="me-profile-team-row"
                      data-team-id={team.id}
                      data-default={isDefault ? "true" : undefined}
                      data-archived={archived ? "true" : undefined}
                      className={
                        archived
                          ? "profile-team-row text-ink-muted opacity-70"
                          : "profile-team-row"
                      }
                    >
                      <span className="profile-team-avatar" aria-hidden="true">
                        {getInitials(team.name).slice(0, 1)}
                      </span>
                      <div className="profile-team-copy">
                        <strong>{team.name}</strong>
                        <span>{roleLabels.join(" + ")}</span>
                      </div>
                      {archived ? (
                        <Badge
                          variant="muted"
                          data-testid="me-profile-team-archived"
                        >
                          Archived
                        </Badge>
                      ) : offerSetDefault ? (
                        <button
                          type="button"
                          onClick={() => onSetDefault(team.id)}
                          disabled={isDefault || saving}
                          data-testid={`me-profile-team-default-${team.id}`}
                          className="profile-team-default-action"
                        >
                          {pending
                            ? "Saving..."
                            : isDefault
                              ? "Default"
                              : "Set default"}
                        </button>
                      ) : (
                        <span className="profile-team-default-label">
                          Default
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="mt-3 rounded border border-rule bg-surface px-3 py-2 text-sm text-ink-muted">
          You are not assigned to any teams yet.
        </p>
      )}
      <p className="profile-team-note">
        Your club manages roles and player links.
      </p>
      <Link to="/hub" className="profile-team-workspace-link">
        Open your workspace
      </Link>
    </section>
  );
}

function isArchivedTeam(team: MeTeam): boolean {
  return (team.status ?? "active") === "archived";
}

/** Label of the profile group that collects teams without a season (D9). */
export const PROFILE_LEGACY_SEASON_LABEL = "Unassigned (legacy)";

export type ProfileTeamSeasonGroup = {
  key: string;
  label: string;
  /** `null` for the legacy group. */
  seasonId: string | null;
  teams: MeTeam[];
};

/** #908 — the profile's team rows grouped by each team's OWN season
 * (`MeTeam.season`), newest season first by its start date, teams without
 * a season last under "Unassigned (legacy)". Within a group active teams
 * come before archived ones, which read as history. */
export function profileTeamSeasonGroups(
  me: MePayload,
): ProfileTeamSeasonGroup[] {
  const seasonsById = new Map(me.seasons.map((season) => [season.id, season]));
  const groups = new Map<string, ProfileTeamSeasonGroup>();
  for (const team of me.teams) {
    const season = team.season ?? null;
    const key = season ? season.id : "legacy";
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label: season ? season.name : PROFILE_LEGACY_SEASON_LABEL,
        seasonId: season?.id ?? null,
        teams: [],
      };
      groups.set(key, group);
    }
    group.teams.push(team);
  }
  const startOf = (group: ProfileTeamSeasonGroup) =>
    group.seasonId ? (seasonsById.get(group.seasonId)?.starts_on ?? "") : "";
  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      teams: [
        ...group.teams.filter((team) => !isArchivedTeam(team)),
        ...group.teams.filter(isArchivedTeam),
      ],
    }))
    .sort((a, b) => {
      if (a.seasonId === null) return 1;
      if (b.seasonId === null) return -1;
      const byStart = startOf(b).localeCompare(startOf(a));
      return byStart !== 0 ? byStart : b.label.localeCompare(a.label);
    });
}

export function profileDefaultTeamId(me: MePayload): string | null {
  return me.user.last_team_id || me.active_scope?.team.id || null;
}

export function profileAuthoritySummary(me: MePayload): {
  roleAuthority: {
    kind: "platform" | "team" | "account";
    role: string;
    badge: string;
    summaryLabel: "Access" | "Team role";
  };
  clubAdminClubs: Array<{ id: string; name: string }>;
  clubAuthority: string | null;
} {
  const roleAuthority = me.user.is_global_admin
    ? {
        kind: "platform" as const,
        role: "Super admin",
        badge: "Platform · Super admin",
        summaryLabel: "Access" as const,
      }
    : me.active_scope
      ? {
          kind: "team" as const,
          role: roleLabel(me.active_scope.effective_role),
          badge: `Team · ${roleLabel(me.active_scope.effective_role)}`,
          summaryLabel: "Team role" as const,
        }
      : {
          kind: "account" as const,
          role: roleLabel(me.user.role),
          badge: `Access · ${roleLabel(me.user.role)}`,
          summaryLabel: "Access" as const,
        };
  // Display authority comes from the full cross-club list. The boolean
  // `is_club_admin` is host-scoped and deliberately false on apex/admin hosts.
  const clubAdminClubs = (me.club_memberships ?? [])
    .filter((membership) => membership.role === "club_admin")
    .map((membership) => ({
      id: membership.club_id,
      name: membership.club_name || membership.club_slug || "Club",
    }));
  const clubAuthority =
    clubAdminClubs.length === 0
      ? null
      : clubAdminClubs.length === 1
        ? `Club admin · ${clubAdminClubs[0].name}`
        : `Club admin · ${clubAdminClubs.length} clubs`;

  return { roleAuthority, clubAdminClubs, clubAuthority };
}

export function profileTeamRoleLabels(me: MePayload, team: MeTeam): string[] {
  const roles = me.memberships
    .filter((membership) => membership.team_id === team.id)
    .map((membership) => roleLabel(membership.role));
  const uniqueRoles = Array.from(new Set(roles));
  return uniqueRoles.length > 0
    ? uniqueRoles
    : [roleLabel(team.membership_role)];
}

function TextField({
  label,
  value,
  onChange,
  testid,
  type = "text",
  autoComplete,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
  type?: string;
  autoComplete?: string;
  maxLength?: number;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-xs">
      <span className="font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        maxLength={maxLength}
        data-testid={testid}
        className="h-10 rounded border border-rule bg-field px-3 text-sm text-ink transition-colors placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"
      />
    </label>
  );
}

function PreferenceToggle({
  label,
  description,
  checked,
  disabled,
  onChange,
  "data-testid": testid,
}: {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  "data-testid"?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-display text-sm text-ink">{label}</div>
        {description ? (
          <p className="mt-0.5 text-xs leading-snug text-ink-muted">
            {description}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        data-checked={checked}
        className={[
          "relative h-6 w-11 shrink-0 rounded-full border transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-50",
          checked ? "border-accent bg-accent" : "border-rule bg-surface",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "absolute top-0.5 h-4 w-4 rounded-full transition-all",
            checked ? "left-[1.5rem] bg-accent-ink" : "left-0.5 bg-ink",
          ].join(" ")}
        />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// B4 — Security tab
// ---------------------------------------------------------------------------

function SecurityTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const security = useQuery({
    queryKey: unscopedKey("me-security"),
    queryFn: getMeSecurity,
  });
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
  });
  const [mfaEnrollHint, setMfaEnrollHint] = useState(false);
  const updateSecurity = useMutation({
    mutationFn: (mfaEnabled: boolean) =>
      patchMeSecurity({ mfa_enabled: mfaEnabled }),
    onSuccess: (payload) => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("me-security"),
      });
      toast.success(
        payload.mfa_user_enabled
          ? "MFA at sign-in enabled"
          : "MFA at sign-in disabled",
      );
    },
    onError: (err) => {
      const code =
        err instanceof ApiError &&
        err.detail &&
        typeof err.detail === "object" &&
        "code" in err.detail
          ? String((err.detail as { code: unknown }).code)
          : "";
      if (code === "mfa_required") {
        // Disabling needs a fresh second factor — the apiFetch interceptor is
        // already navigating to /login?aal=aal2&next=… so a toast would just
        // flash during the redirect.
        return;
      }
      if (code === "mfa_enrollment_required") {
        setMfaEnrollHint(true);
        return;
      }
      toast.error("Couldn't update MFA", { description: formatErr(err) });
    },
  });
  const canUseLegacyPassword = authMode.data?.legacy_enabled === true;
  const securityData = security.data;

  const handleMfaToggle = (next: boolean) => {
    if (updateSecurity.isPending) return;
    if (next && securityData && !securityData.second_factor_enrolled) {
      // Client-side short-circuit of the backend's mfa_enrollment_required
      // guard: point at the enrollment sections below instead of a round-trip.
      setMfaEnrollHint(true);
      return;
    }
    setMfaEnrollHint(false);
    updateSecurity.mutate(next);
  };

  const mfaControl = (
    <div className="security-mfa-card" data-testid="me-security-mfa-card">
      <h3>Multi-factor sign-in</h3>
      <PreferenceToggle
        label="Require MFA at sign-in"
        description="Password sign-ins ask for your authenticator code as a second step. Passkey and Google sign-ins already count as strong and skip the prompt."
        checked={!!securityData?.mfa_enabled}
        disabled={
          updateSecurity.isPending ||
          !!securityData?.mfa_required_by_role ||
          !securityData?.kratos_linked
        }
        onChange={handleMfaToggle}
        data-testid="me-security-mfa-toggle"
      />
      {mfaEnrollHint ? (
        <p
          data-testid="me-security-mfa-enroll-hint"
          className="mt-3 rounded border border-flag/40 bg-flag/10 px-3 py-2 text-xs text-ink"
        >
          Add an authenticator app or backup codes below first — the sign-in
          prompt needs a second factor to ask for.
        </p>
      ) : null}
      {securityData?.mfa_required_by_role ? (
        <p className="mt-3 rounded border border-good/30 bg-good/10 px-3 py-2 text-xs text-good">
          MFA is required for your role and cannot be disabled.
        </p>
      ) : null}
      {securityData?.mfa_enabled && !securityData?.second_factor_enrolled ? (
        <p
          data-testid="me-security-mfa-unenrolled-warning"
          className="mt-3 rounded border border-flag/40 bg-flag/10 px-3 py-2 text-xs text-ink"
        >
          MFA is on but no second factor is enrolled, so sign-ins can't be
          challenged. Add an authenticator app or backup codes below.
        </p>
      ) : null}
      {securityData?.mfa_user_enabled && !securityData?.session_mfa_strong ? (
        <p className="mt-3 font-mono text-[0.65rem] leading-relaxed text-ink-muted">
          Turning this off will first ask for your authenticator code.
        </p>
      ) : null}
    </div>
  );

  if (authMode.data?.kratos_enabled) {
    return (
      <section
        data-testid="me-security-section"
        className="flex flex-col gap-4"
      >
        <SectionHeader
          icon={<ShieldCheck size={18} strokeWidth={2.2} />}
          title="Security"
          description="Manage passwords, passkeys, multi-factor controls, and social sign-in for your account."
        />
        {security.isLoading ? (
          <p className="rounded border border-rule bg-elevated px-3 py-2 text-sm text-ink-muted">
            Checking account security...
          </p>
        ) : (
          <>
            <KratosSettingsPanel
              socialConnections={securityData?.social_connections ?? []}
              securitySummary={securityData}
              mfaControl={mfaControl}
            />
            {securityData?.methods_error ? (
              <p
                data-testid="me-security-methods-error"
                className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              >
                {securityData.methods_error}
              </p>
            ) : null}
          </>
        )}
      </section>
    );
  }

  return (
    <section data-testid="me-security-section" className="flex flex-col gap-4">
      <SectionHeader
        icon={<ShieldCheck size={18} strokeWidth={2.2} />}
        title="Security"
        description="Update the legacy password for your account."
      />
      {security.isLoading ? (
        <p className="rounded border border-rule bg-elevated px-3 py-2 text-sm text-ink-muted">
          Checking account security...
        </p>
      ) : null}
      {securityData?.mfa_required_by_role ? (
        <p className="rounded border border-good/30 bg-good/10 px-3 py-2 text-xs text-good">
          MFA is required for your role.
        </p>
      ) : null}
      {canUseLegacyPassword ? <LegacyPasswordForm /> : null}
      {!authMode.isPending && !canUseLegacyPassword ? (
        <p
          data-testid="me-security-unavailable"
          className="rounded border border-rule bg-elevated px-3 py-2 text-sm text-ink-muted"
        >
          Password changes are not available for this sign-in method. Use
          account recovery to set a new password.
        </p>
      ) : null}
    </section>
  );
}

function LegacyPasswordForm() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      changeMePassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setSuccess(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Password updated");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Current password is incorrect.",
        );
      } else if (err instanceof ApiError && err.status === 403) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Password changes are only available for database users.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't change password", { description: formatErr(err) });
    },
  });

  return (
    <form
      data-testid="me-password-form"
      onSubmit={(e) => {
        e.preventDefault();
        setSuccess(false);
        setError(null);
        if (next.length < 8) {
          setError("New password must be at least 8 characters.");
          return;
        }
        if (next !== confirm) {
          setError("New password and confirmation don't match.");
          return;
        }
        mut.mutate();
      }}
      className="flex flex-col gap-4"
    >
      <p className="rounded border border-rule bg-elevated px-3 py-2 text-xs text-ink-muted">
        This changes your legacy Tktic password only. External passwords, MFA,
        and passkeys stay managed by secure sign-in.
      </p>
      <TextField
        label="Current password"
        type="password"
        value={current}
        onChange={setCurrent}
        testid="me-password-current"
        autoComplete="current-password"
      />
      <TextField
        label="New password"
        type="password"
        value={next}
        onChange={setNext}
        testid="me-password-new"
        autoComplete="new-password"
      />
      <TextField
        label="Confirm new password"
        type="password"
        value={confirm}
        onChange={setConfirm}
        testid="me-password-confirm"
        autoComplete="new-password"
      />

      {error ? (
        <p
          data-testid="me-password-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="me-password-success"
          className="text-good text-xs font-mono"
        >
          Password updated. Other sessions have been signed out.
        </p>
      ) : null}

      <div className="flex justify-end border-t border-rule pt-4">
        <Button
          type="submit"
          size="sm"
          disabled={mut.isPending}
          data-testid="me-password-save"
        >
          <KeyRound size={15} strokeWidth={2.2} />
          {mut.isPending ? "Saving…" : "Update password"}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// B5 — Email tab
// ---------------------------------------------------------------------------

function EmailTab({ me }: { me: MePayload }) {
  const profile = me.profile;
  const toast = useToast();
  const verified = !!profile.email_verified_at;
  const hasEmail = !!profile.email;
  const [devToken, setDevToken] = useState<string | null>(null);
  const [token, setToken] = useState("");
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [confirmSuccess, setConfirmSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const applyAuth = useAuthStore((s) => s.applyMePayload);
  const applyScope = useScopeStore((s) => s.applyMePayload);

  const mergeProfileIntoMe = (nextProfile: UserProfile) => {
    const current =
      queryClient.getQueryData<MePayload>(unscopedKey("me")) ?? me;
    const nextMe = { ...current, profile: nextProfile };
    queryClient.setQueryData(unscopedKey("me"), nextMe);
    applyAuth(nextMe);
    applyScope(nextMe);
  };

  const requestMut = useMutation({
    mutationFn: requestMeEmailVerification,
    onSuccess: (resp) => {
      setRequestSuccess(true);
      // Privacy invariant: dev-token surfaces ONLY when the backend
      // returns it (TKTIC_DEV_TOKEN_DELIVERY=1). Never logged.
      if (resp.verification_token) {
        setDevToken(resp.verification_token);
      }
      toast.success("Verification email sent");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Add an email on the Profile tab before requesting verification.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't send verification email", {
        description: formatErr(err),
      });
    },
  });

  const confirmMut = useMutation({
    mutationFn: (t: string) => confirmEmailVerification({ token: t }),
    onSuccess: () => {
      setConfirmSuccess(true);
      setDevToken(null);
      setToken("");
      void queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      toast.success("Email verified");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "That verification token is invalid or expired.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't verify email", { description: formatErr(err) });
    },
  });

  const emailPreferenceMut = useMutation({
    mutationFn: (body: {
      email_notify_feedback_digest?: boolean;
      email_notify_access_requests?: boolean;
    }) => patchMeProfile(body),
    onSuccess: (payload) => {
      mergeProfileIntoMe(payload.profile);
      toast.success("Email preferences saved");
    },
    onError: (err) => {
      toast.error("Couldn't save email preferences", {
        description: formatErr(err),
      });
    },
  });

  return (
    <section data-testid="me-email-section" className="account-two-column">
      <div className="account-main-stack">
        <section className="account-pane">
          <div className="account-pane-heading">
            <h2>Email address</h2>
            <Mail aria-hidden="true" />
          </div>
          <div className="account-email-address">
            <strong>{profile.email || "No email on file"}</strong>
            {hasEmail ? (
              <span
                data-testid="me-email-verified-pill"
                data-verified={verified ? "true" : "false"}
              >
                {verified ? "Verified" : "Unverified"}
              </span>
            ) : null}
          </div>
          <p className="account-support-copy">
            Edit your email address on the Profile tab.
          </p>
          <div className="account-actions">
            <Link
              to="/me"
              search={{ tab: "profile" }}
              className="account-tonal-link"
            >
              Edit email
            </Link>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!hasEmail || verified || requestMut.isPending}
              onClick={() => {
                setRequestSuccess(false);
                setError(null);
                requestMut.mutate();
              }}
              data-testid="me-email-request"
            >
              {requestMut.isPending ? "Sending…" : "Send verification email"}
            </Button>
          </div>
          {requestSuccess ? (
            <p
              data-testid="me-email-request-success"
              className="text-xs text-good"
            >
              Verification email sent.
            </p>
          ) : null}
        </section>

        <section
          className="account-pane"
          data-testid="me-email-notification-preferences"
        >
          <div className="account-pane-heading">
            <h2>Email preferences</h2>
            <BellRing aria-hidden="true" />
          </div>
          <p className="account-support-copy">
            Choose which updates arrive in your inbox.
          </p>
          <div className="account-preference-list">
            <PreferenceToggle
              label="Daily feedback digest"
              description="A daily summary of newly shared feedback."
              checked={isFeedbackDigestEmailEnabled(profile)}
              disabled={emailPreferenceMut.isPending}
              onChange={(next) =>
                emailPreferenceMut.mutate({
                  email_notify_feedback_digest: next,
                })
              }
              data-testid="me-email-feedback-digest-toggle"
            />
            <PreferenceToggle
              label="Team access request updates"
              description="Updates about team access requests."
              checked={isAccessRequestEmailEnabled(profile)}
              disabled={emailPreferenceMut.isPending}
              onChange={(next) =>
                emailPreferenceMut.mutate({
                  email_notify_access_requests: next,
                })
              }
              data-testid="me-email-access-request-toggle"
            />
          </div>
        </section>

        {!verified ? (
          <section className="account-pane account-verification-pane">
            <h2>Confirm email</h2>
            {devToken ? (
              <div
                data-testid="me-email-dev-token"
                className="account-dev-token"
              >
                <span>Dev verification token</span>
                <code>{devToken}</code>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard?.writeText(devToken);
                    setToken(devToken);
                  }}
                  data-testid="me-email-dev-copy"
                >
                  Copy + paste into the confirm field
                </button>
              </div>
            ) : null}
            <label>
              <span>Verification token</span>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                data-testid="me-email-token"
              />
            </label>
            <Button
              type="button"
              size="sm"
              disabled={token.length < 16 || confirmMut.isPending}
              onClick={() => {
                setConfirmSuccess(false);
                setError(null);
                confirmMut.mutate(token.trim());
              }}
              data-testid="me-email-confirm"
            >
              <CheckCircle2 size={15} strokeWidth={2.2} />
              {confirmMut.isPending ? "Confirming…" : "Confirm email"}
            </Button>
            {confirmSuccess ? (
              <p
                data-testid="me-email-confirm-success"
                className="text-xs text-good"
              >
                Email verified. Thank you for confirming.
              </p>
            ) : null}
          </section>
        ) : null}
        {error ? (
          <p data-testid="me-email-error" className="text-xs text-destructive">
            {error}
          </p>
        ) : null}
      </div>
      <aside className="account-cyan-panel">
        <h2>Keep in touch.</h2>
        <p>
          A verified email helps you recover your account and receive team
          updates.
        </p>
        <Link
          to="/me"
          search={{ tab: "security" }}
          className="account-tonal-link"
        >
          Review sign-in security
        </Link>
      </aside>
    </section>
  );
}

// ---------------------------------------------------------------------------
// B6 — Sessions tab (minimal per locked scope; full list 🔵)
// ---------------------------------------------------------------------------

function SessionsTab() {
  const navigate = useNavigate({ from: "/me" });
  const toast = useToast();
  const [signOutPending, setSignOutPending] = useState(false);
  const [signOutAllPending, setSignOutAllPending] = useState(false);
  const security = useQuery({
    queryKey: unscopedKey("me-security"),
    queryFn: getMeSecurity,
  });

  const onSignOut = async () => {
    if (signOutPending || signOutAllPending) return;
    setSignOutPending(true);
    try {
      // /api/logout revokes the app session AND the Kratos session.
      await apiLogout();
    } catch {
      /* server may already have killed the session; clear locally */
    } finally {
      applyExplicitLogoutBoundary();
      void navigate({ to: "/login", replace: true });
    }
  };

  const onSignOutEverywhere = async () => {
    if (signOutPending || signOutAllPending) return;
    setSignOutAllPending(true);
    let shouldClearLocalSession = false;
    try {
      try {
        await revokeAllMeSessions();
        shouldClearLocalSession = true;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          shouldClearLocalSession = true;
        } else {
          toast.error("Couldn't sign out everywhere", {
            description: formatErr(err),
          });
        }
      }
    } finally {
      if (shouldClearLocalSession) {
        applyExplicitLogoutBoundary();
        void navigate({ to: "/login", replace: true });
      } else {
        setSignOutAllPending(false);
      }
    }
  };

  return (
    <section data-testid="me-sessions-section" className="account-two-column">
      <div className="account-main-stack">
        <TrustedDevicesPanel />
        <section className="account-pane">
          <h2>Sign out</h2>
          <p className="account-support-copy">
            Signing out everywhere also forgets every remembered browser.
          </p>
          <div className="account-actions">
            <button
              type="button"
              onClick={() => void onSignOut()}
              disabled={signOutPending || signOutAllPending}
              data-testid="me-sessions-signout"
              className="account-tonal-link"
            >
              {signOutPending ? "Signing out…" : "Sign out this browser"}
            </button>
            <button
              type="button"
              onClick={() => void onSignOutEverywhere()}
              disabled={signOutPending || signOutAllPending}
              data-testid="me-sessions-signout-all"
              className="account-danger-link"
            >
              <LockKeyhole size={15} strokeWidth={2.2} />
              {signOutAllPending ? "Signing out…" : "Sign out everywhere"}
            </button>
          </div>
        </section>
      </div>
      <aside className="account-side-stack">
        <SignInMethodsPanel
          loading={security.isLoading}
          security={security.data}
        />
      </aside>
    </section>
  );
}

function TrustedDevicesPanel() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const devices = useQuery({
    queryKey: unscopedKey("me-trusted-devices"),
    queryFn: getMeTrustedDevices,
  });
  const revokeDevice = useMutation({
    mutationFn: (deviceId: string) => revokeMeTrustedDevice(deviceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("me-trusted-devices"),
      });
      toast.success("Device forgotten", {
        description: "That browser will be asked for a code at next sign-in.",
      });
    },
    onError: (err) => {
      toast.error("Couldn't forget the device", {
        description: formatErr(err),
      });
    },
  });

  const rows = devices.data?.devices ?? [];
  const collection = useCollection(rows, {
    scope: "remembered-devices",
    text: (d) => d.user_agent ?? "Unknown browser",
  });
  const formatDay = (epochSeconds: number | null | undefined) =>
    epochSeconds ? new Date(epochSeconds * 1000).toLocaleDateString() : "—";

  return (
    <div
      data-testid="me-trusted-devices"
      className="rounded border border-rule bg-elevated p-3"
    >
      <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
        Remembered browsers
      </p>
      <p className="mt-2 text-sm leading-snug text-ink-muted">
        These browsers can skip the extra MFA prompt until their remembered
        period expires. This is not a list of every active session.
      </p>
      <CollectionToolbar collection={collection} label="remembered browsers" />
      {devices.isLoading ? (
        <p className="mt-3 font-mono text-xs text-ink-muted">
          Checking devices…
        </p>
      ) : rows.length === 0 ? (
        <p
          data-testid="me-trusted-devices-empty"
          className="mt-3 font-mono text-xs text-ink-muted"
        >
          No remembered browsers.
        </p>
      ) : (
        <ul className="remembered-browser-list">
          {collection.items.map((device) => (
            <li
              key={device.id}
              data-testid="me-trusted-device-row"
              className="remembered-browser-row"
            >
              <Laptop size={18} strokeWidth={2} aria-hidden="true" />
              <div className="remembered-browser-copy">
                <p title={device.user_agent}>
                  {friendlyDeviceName(device.user_agent)}
                  {device.current ? <span>This browser</span> : null}
                </p>
                <small>Remembered {formatDay(device.created_at)}</small>
                <small>Expires {formatDay(device.expires_at)}</small>
              </div>
              <button
                type="button"
                onClick={() => revokeDevice.mutate(device.id)}
                disabled={revokeDevice.isPending}
                data-testid="me-trusted-device-revoke"
                className="remembered-browser-forget"
              >
                Forget
              </button>
            </li>
          ))}
        </ul>
      )}
      <CollectionPagination collection={collection} />
    </div>
  );
}

function friendlyDeviceName(userAgent: string | null | undefined): string {
  const ua = userAgent ?? "";
  const browser = /Edg\//i.test(ua)
    ? "Edge"
    : /Chrome\//i.test(ua)
      ? "Chrome"
      : /Safari\//i.test(ua)
        ? "Safari"
        : /Firefox\//i.test(ua)
          ? "Firefox"
          : "Browser";
  const platform = /iPhone/i.test(ua)
    ? "iPhone"
    : /iPad/i.test(ua)
      ? "iPad"
      : /Mac OS|Macintosh/i.test(ua)
        ? "macOS"
        : /Android/i.test(ua)
          ? "Android"
          : /Windows/i.test(ua)
            ? "Windows"
            : "this device";
  return `${browser} on ${platform}`;
}

function SignInMethodsPanel({
  loading,
  security,
}: {
  loading: boolean;
  security?: MeSecurityPayload;
}) {
  const passkeyCount = (security?.authentication_methods ?? []).filter(
    (method) => {
      const type = method.type.trim().toLowerCase();
      return type === "passkey" || type.startsWith("webauthn");
    },
  ).length;
  const social = security?.social_connections ?? [];
  const methodRows = [
    {
      key: "password",
      label: "Password",
      value: security?.password_set === false ? "Not set" : "Set",
      icon: <KeyRound size={18} />,
    },
    {
      key: "passkeys",
      label: "Passkeys",
      value: String(passkeyCount),
      icon: <Fingerprint size={18} />,
    },
    ...(["google", "apple"] as const).map((provider) => {
      const connection = social.find((item) => item.provider === provider);
      return {
        key: provider,
        label: provider === "google" ? "Google" : "Apple",
        value: connection?.connected ? "Connected" : "Not connected",
        icon: <Link2 size={18} />,
      };
    }),
  ];
  return (
    <section
      data-testid="me-signin-methods"
      className="rounded border border-rule bg-elevated p-3"
    >
      <h2>Sign-in methods</h2>

      {loading ? (
        <p
          data-testid="me-signin-methods-loading"
          className="mt-3 rounded border border-rule bg-surface px-3 py-2 text-sm text-ink-muted"
        >
          Loading sign-in methods…
        </p>
      ) : (
        <div className="signin-method-list">
          {methodRows.map((method) => (
            <div
              key={method.key}
              data-testid="me-signin-method-row"
              className="signin-method-row"
            >
              <span className="signin-method-icon" aria-hidden="true">
                {method.icon}
              </span>
              <strong>{method.label}</strong>
              <span
                data-status={
                  method.value === "Set" || method.value === "Connected"
                    ? "good"
                    : undefined
                }
              >
                {method.value}
              </span>
            </div>
          ))}
        </div>
      )}
      <Link
        to="/me"
        search={{ tab: "security" }}
        className="signin-method-manage"
      >
        Manage security
      </Link>
    </section>
  );
}

export function authIdentityProviderLabel(provider: string): string {
  const clean = provider.trim().toLowerCase();
  if (clean === "kratos") return "Secure sign-in";
  if (!clean) return "Unknown";
  return clean
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function authMethodTypeLabel(type: string): string {
  const clean = type.trim().toLowerCase();
  if (clean === "totp") return "Authenticator app";
  if (clean === "passkey") return "Passkey";
  if (clean === "webauthn-platform") return "Device passkey";
  if (clean === "webauthn-roaming") return "Security key";
  if (!clean) return "Security method";
  return clean
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function isTotpMethod(method: AuthenticationMethodSummary): boolean {
  return method.type.trim().toLowerCase() === "totp";
}

export function isPasskeyMethod(method: AuthenticationMethodSummary): boolean {
  return method.type.trim().toLowerCase() === "passkey";
}

export function isWebAuthnMethod(method: AuthenticationMethodSummary): boolean {
  const clean = method.type.trim().toLowerCase();
  return clean === "webauthn-platform" || clean === "webauthn-roaming";
}

export function formatAuthIdentityTime(timestamp: number): string {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "unknown";
  return new Date(timestamp * 1000).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Network error. Try again.";
}

function getInitials(label: string): string {
  const cleaned = label.trim();
  if (!cleaned) return "AC";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}

function roleLabel(role: string): string {
  return role
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function AccountIdentityStrip({ me }: { me: MePayload }) {
  const [expanded, setExpanded] = useState(false);
  const display =
    me.user.display_name || me.profile.display_name || me.user.username;
  const photo = useAccountPhoto(me.profile.photo_filename);
  const authority = profileAuthoritySummary(me);
  const roleTestId =
    authority.roleAuthority.kind === "platform"
      ? "me-authority-platform"
      : authority.roleAuthority.kind === "team"
        ? "me-authority-team"
        : "me-authority-access";
  const hasComplexAuthority =
    me.teams.length > 1 || !!authority.clubAuthority || me.user.is_global_admin;
  return (
    <div className="account-identity">
      <div className="account-identity-row">
        {photo ? (
          <img src={photo} alt="" />
        ) : (
          <span className="account-identity-avatar">
            {getInitials(display)}
          </span>
        )}
        <div>
          <strong>{display}</strong>
          <small>{me.profile.email || `@${me.user.username}`}</small>
        </div>
        <div className="account-identity-badges">
          <StatusPill tone="accent">{authority.roleAuthority.role}</StatusPill>
          <StatusPill tone={me.profile.email_verified_at ? "good" : "muted"}>
            {me.profile.email_verified_at
              ? "Email verified"
              : "Email unverified"}
          </StatusPill>
          <span className="sr-only" data-testid={roleTestId}>
            {authority.roleAuthority.badge}
          </span>
          {authority.clubAuthority ? (
            <span className="sr-only" data-testid="me-authority-club">
              {authority.clubAuthority}
            </span>
          ) : null}
        </div>
        {hasComplexAuthority ? (
          <Button
            variant="ghost"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Hide account details" : "Account details"}
          </Button>
        ) : null}
      </div>
      {expanded ? <AccountSummary me={me} /> : null}
    </div>
  );
}
